/**
 * VM-14b — Kautionskonto (Deposit) mit Ratenplan, Bewegungen und Verzinsung.
 * Extrahiert aus TenancyDetail.jsx.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { formatEUR, formatDate } from "@/lib/constants";
import { Info as InfoIcon, Warning, Plus, Trash, UserCircle, ShieldCheck, DotsThreeVertical, ArrowsLeftRight, ShieldStar, FileText } from "@phosphor-icons/react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Th, Td, StatCard } from "./TenancyBits";

// Sprint 5h — Kautionskonto: tagesgenaue Verzinsung, 3-Raten-Fälligkeitsplan, Bewegungen erfassen
export default function DepositAccountPanel({ tenancyId, tenancyType, tenancyEnded = false, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newMovement, setNewMovement] = useState({ booking_date: new Date().toISOString().slice(0, 10), amount: "", kind: "payment", reference: "" });
  // 2026-02 — Drittzahler-Dialog
  const [tpDialog, setTpDialog] = useState(null);
  // 2026-02 — Abtretungs-Warndialog vor Auszahlung
  const [payoutWarn, setPayoutWarn] = useState(false);
  // iter163b — Bürgschaft erfassen (statt Barkaution)
  const [guaranteeDialog, setGuaranteeDialog] = useState(false);
  const [guarantee, setGuarantee] = useState({ issuer: "", reference: "", valid_until: "", received_date: new Date().toISOString().slice(0, 10), note: "" });
  const [guaranteeFile, setGuaranteeFile] = useState(null);
  const submitGuarantee = async () => {
    try {
      let documentId = null;
      if (guaranteeFile) {
        const fd = new FormData();
        fd.append("file", guaranteeFile);
        fd.append("title", `Bürgschaftsurkunde ${guarantee.issuer || ""}`.trim());
        fd.append("category", "Kaution");
        fd.append("linked_tenancy_id", tenancyId);
        const { data: up } = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        documentId = up?.id || up?.document?.id || null;
      }
      await api.post(`/deposit-accounts/${tenancyId}/guarantee`, {
        amount: guarantee.amount ? Number(guarantee.amount) : null,
        issue_date: guarantee.issue_date || null,
        guarantee_type: guarantee.guarantee_type || null,
        document_id: documentId,
        issuer: guarantee.issuer.trim() || null,
        reference: guarantee.reference.trim() || null,
        valid_until: guarantee.valid_until || null,
        received_date: guarantee.received_date || null,
        note: guarantee.note.trim() || null,
      });
      toast.success("Bürgschaft erfasst — Kautionskonto ist gedeckt");
      setGuaranteeDialog(false);
      setGuaranteeFile(null);
      setGuarantee({ issuer: "", reference: "", valid_until: "", received_date: new Date().toISOString().slice(0, 10), note: "" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bürgschaft konnte nicht erfasst werden");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/deposit-accounts/${tenancyId}`);
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kautionskonto konnte nicht geladen werden");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenancyId]);

  const addMovement = async () => {
    if (!newMovement.amount) { toast.error("Bitte Betrag eingeben"); return; }
    // 2026-02 — Bei Auszahlung: Warnhinweis auf Abtretung wenn Drittzahler ohne
    // vorliegende Abtretungserklärung existieren.
    if (newMovement.kind === "payout"
        && data?.has_third_party_payer
        && !data?.third_party_all_assignments_received) {
      setPayoutWarn(true);
      return;
    }
    try {
      await api.post(`/deposit-accounts/${tenancyId}/movements`, {
        booking_date: newMovement.booking_date,
        amount: parseFloat(String(newMovement.amount).replace(",", ".")),
        kind: newMovement.kind,
        reference: newMovement.reference || null,
      });
      toast.success("Kautionsbewegung erfasst");
      setNewMovement({ booking_date: new Date().toISOString().slice(0, 10), amount: "", kind: "payment", reference: "" });
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Erfassen"); }
  };
  const confirmPayoutAnyway = async () => {
    setPayoutWarn(false);
    try {
      await api.post(`/deposit-accounts/${tenancyId}/movements`, {
        booking_date: newMovement.booking_date,
        amount: parseFloat(String(newMovement.amount).replace(",", ".")),
        kind: newMovement.kind,
        reference: (newMovement.reference || "") + " · Auszahlung trotz fehlender Abtretung",
      });
      toast.success("Auszahlung erfasst");
      setNewMovement({ booking_date: new Date().toISOString().slice(0, 10), amount: "", kind: "payment", reference: "" });
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Erfassen"); }
  };
  const deleteMovement = async (mid) => {
    if (!await window._domivusConfirm("Diese Kautionsbewegung wirklich löschen?")) return;
    try {
      await api.delete(`/deposit-accounts/${tenancyId}/movements/${mid}`);
      toast.success("Bewegung gelöscht");
      load();
      if (typeof onChanged === "function") onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Löschen"); }
  };

  if (loading) return <div className="text-sm text-slate-500">Lade Kautionskonto…</div>;
  if (!data) return <div className="text-sm text-slate-500">Kein Kautionskonto verfügbar.</div>;

  const statusBadge = (s) => {
    const map = {
      paid: { label: "Bezahlt", cls: "bg-emerald-100 text-emerald-800" },
      paid_late: { label: "Bezahlt (verspätet)", cls: "bg-emerald-50 text-emerald-700 border border-emerald-200" },
      pending: { label: "Offen", cls: "bg-slate-100 text-slate-700" },
      overdue: { label: "Überfällig", cls: "bg-rose-100 text-rose-800" },
      unknown: { label: "?", cls: "bg-slate-100 text-slate-700" },
    };
    return map[s] || map.unknown;
  };

  return (
    <div className="space-y-3" data-testid="deposit-panel">
      {/* 2026-03 — Overflow-Menü mit „versteckten" Wartungs-Aktionen */}
      <div className="flex items-center justify-end -mb-1">
        <DepositActionsMenu tenancyId={tenancyId} data={data} onChanged={() => { load(); onChanged?.(); }} />
      </div>
      {/* 2026-02 — Warnbanner: Drittzahler vorhanden */}
      {data.has_third_party_payer && (
        <div className={`flex items-start gap-3 rounded-md p-3 ${
          data.third_party_all_assignments_received
            ? "bg-amber-50 border border-amber-200"
            : "bg-rose-50 border border-rose-200"
        }`} data-testid="deposit-third-party-banner">
          <UserCircle size={20} className={data.third_party_all_assignments_received ? "text-amber-600 shrink-0" : "text-rose-600 shrink-0"} />
          <div className="flex-1 text-sm">
            <div className={`font-semibold ${data.third_party_all_assignments_received ? "text-amber-900" : "text-rose-900"}`}>
              Kaution (teilweise) von Drittzahler geleistet
              {data.third_party_sum > 0 && <span className="ml-1 text-xs tabular-nums">({formatEUR(data.third_party_sum)})</span>}
            </div>
            <div className={`text-xs mt-0.5 ${data.third_party_all_assignments_received ? "text-amber-800" : "text-rose-800"}`}>
              {data.third_party_payer_names.length > 0 && (
                <>Drittzahler: <span className="font-medium">{data.third_party_payer_names.join(", ")}</span> · </>
              )}
              {data.third_party_all_assignments_received
                ? "Abtretungserklärung liegt für alle Drittzahler vor — Auszahlung an Mieter zulässig."
                : "Vor Auszahlung an den Mieter: Abtretungserklärung(en) prüfen! Ohne Abtretung ist die Auszahlung nur an den Drittzahler zulässig."
              }
            </div>
          </div>
        </div>
      )}

      {/* iter160i — Kautionsabrechnung § 551 BGB nur nach MV-Ende sichtbar */}
      {tenancyEnded && (
        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-md p-3" data-testid="deposit-settlement-link">
          <div className="text-sm">
            <div className="font-semibold text-emerald-900">Kautionsabrechnung (§ 551 BGB)</div>
            <div className="text-xs text-emerald-800 mt-0.5">
              Tagesgenau verzinste Endabrechnung mit Eigentümer-Freigabe.
            </div>
          </div>
          <a href={`/kaution?tenancy_id=${tenancyId}`}
             className="text-xs px-3 py-2 rounded bg-emerald-700 hover:bg-emerald-800 text-white font-semibold whitespace-nowrap"
             data-testid="deposit-settlement-goto">
            Zur Abrechnung →
          </a>
        </div>
      )}

      {/* 2026-03 — Redundantes "Kaution wurde bisher NICHT geleistet"-Banner entfernt
          (die identische Warnung erscheint bereits im Mietkonto-Tab oben als
          "Kaution nicht bezahlt"-Header). */}

      {/* iter160h — Positiv-Banner wenn Kaution voll bezahlt */}
      {data.paid_in_full && !data.warning_overdue_installment && (
        <div className="flex items-start gap-2 bg-emerald-50 border border-emerald-200 rounded p-3 text-sm text-emerald-800" data-testid="deposit-status-paid">
          <InfoIcon size={18} weight="fill" className="shrink-0 text-emerald-700 mt-0.5" />
          <div>
            <div className="font-semibold">
              {data.hide_installments
                ? "Kaution vollständig bezahlt (Einmalzahlung)."
                : "Kaution vollständig eingegangen."}
            </div>
          </div>
        </div>
      )}
      {data.notice_installment_mode && (
        <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-900" data-testid="deposit-notice-installment">
          <InfoIcon size={18} className="shrink-0 text-blue-700 mt-0.5" />
          <div>
            {tenancyType === "01"
              ? "Mieter zahlt in Raten laut § 551 BGB — Termine werden eingehalten."
              : "Mieter zahlt Kaution in Raten — Termine werden eingehalten."}
          </div>
        </div>
      )}

      {/* Übersicht */}
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Kaution laut Vertrag" value={formatEUR(data.deposit_total)} />
        <StatCard label="Aktueller Saldo" value={formatEUR(data.balance)} accent={data.balance < data.deposit_total ? "warn" : "ok"} />
        <StatCard label={`Zinsen aufgelaufen (${data.interest_rate_percent}% p.a.)`} value={formatEUR(data.interest_accrued)} />
        <StatCard label="Differenz" value={formatEUR(data.deposit_total - data.balance)} accent={(data.deposit_total - data.balance) > 0.01 ? "warn" : "ok"} />
      </div>

      {/* Ratenplan */}
      {(data.installment_schedule || []).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold uppercase text-slate-700">
            {tenancyType === "01" ? "3-Raten-Fälligkeitsplan (§ 551 BGB)" : "Raten-Fälligkeitsplan"}
          </div>
          <table className="w-full text-sm">
            <thead className="text-left">
              <tr>
                <Th>Rate</Th><Th>Fälligkeit</Th><Th className="text-right">Betrag</Th><Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {data.installment_schedule.map((r) => {
                const s = statusBadge(r.status);
                return (
                  <tr key={r.index} className="border-t border-slate-100" data-testid={`deposit-installment-${r.index}`}>
                    <Td>{r.index}. Rate</Td>
                    <Td className="tabular-nums text-xs">{formatDate(r.due_date)}</Td>
                    <Td className="text-right tabular-nums">{formatEUR(r.amount)}</Td>
                    <Td><span className={`text-[10px] px-2 py-0.5 rounded-full ${s.cls}`}>{s.label}</span></Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 2026-03 — DepositTransferPanel + ConvertToGuaranteePanel wurden ins Overflow-Menü DepositActionsMenu verschoben */}

      {/* 2026-03 — Historie aller Kaution→Miete-Umbuchungen */}
      <DepositTransferHistory tenancyId={tenancyId} onChanged={() => { load(); onChanged?.(); }} />

      {/* Neue Bewegung erfassen */}
      <div className="bg-white border border-slate-200 rounded-md p-3 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-xs font-semibold uppercase text-slate-700">Neue Bewegung erfassen</div>
          <div className="flex items-center gap-2">
            {/* iter164 — Rückstand aus Kaution verrechnen */}
            {(data?.balance || 0) > 0.01 && (
              <Button
                size="sm" variant="outline"
                className="h-7 gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={async () => {
                  if (!window.confirm("Alle offenen Mietkonto-Rückstände werden aus dem Kautions-Guthaben (FIFO nach Kategorie) verrechnet. Fortfahren?")) return;
                  try {
                    const { data: r } = await api.post(`/deposit-accounts/${tenancyId}/settle-arrears-from-deposit`, {});
                    toast.success(`Verrechnet: ${new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR"}).format(r.settled_total)} — neues Guthaben ${new Intl.NumberFormat("de-DE",{style:"currency",currency:"EUR"}).format(r.new_deposit_balance)}`);
                    load(); onChanged?.();
                  } catch (e) {
                    toast.error(e?.response?.data?.detail || "Verrechnung fehlgeschlagen");
                  }
                }}
                data-testid="deposit-settle-arrears-btn"
                title="Alle offenen Mietkonto-Rückstände aus dem Kautions-Guthaben verrechnen (FIFO nach Kategorie)"
              >
                <ArrowsLeftRight size={13} /> Rückstand verrechnen
              </Button>
            )}
            {!tenancyEnded && !(data?.movements || []).some((m) => m.kind === "guarantee") && (
              <Button
                size="sm" variant="outline"
                className="h-7 gap-1.5 text-teal-700 border-teal-300 hover:bg-teal-50"
                onClick={() => setGuaranteeDialog(true)}
                data-testid="deposit-guarantee-btn"
                title="Statt Barkaution eine Mietkautionsbürgschaft erfassen (deckt die Sollkaution rechtlich ab)"
              >
                <ShieldStar size={13} /> Bürgschaft erfassen
              </Button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-5 gap-2">
          <Input type="date" value={newMovement.booking_date} onChange={(e) => setNewMovement({ ...newMovement, booking_date: e.target.value })} data-testid="deposit-mov-date" />
          <Input type="number" step="0.01" placeholder="Betrag €" value={newMovement.amount} onChange={(e) => setNewMovement({ ...newMovement, amount: e.target.value })} data-testid="deposit-mov-amount" />
          <Select value={newMovement.kind} onValueChange={(v) => setNewMovement({ ...newMovement, kind: v })}>
            <SelectTrigger data-testid="deposit-mov-kind"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="payment">Zahlung Mieter</SelectItem>
              <SelectItem value="payout">Auszahlung</SelectItem>
              <SelectItem value="interest_credit">Zinsgutschrift</SelectItem>
              <SelectItem value="fee">Gebühr</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Notiz (optional)" value={newMovement.reference} onChange={(e) => setNewMovement({ ...newMovement, reference: e.target.value })} className="col-span-1" />
          <Button className="bg-slate-900 hover:bg-slate-800" onClick={addMovement} data-testid="deposit-mov-submit">
            <Plus size={14} className="mr-1" /> Erfassen
          </Button>
        </div>
      </div>

      {/* iter163b — Bürgschaft erfassen Dialog */}
      <Dialog open={guaranteeDialog} onOpenChange={setGuaranteeDialog}>
        <DialogContent className="max-w-lg" data-testid="deposit-guarantee-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldStar size={20} className="text-teal-700" /> Kautionsbürgschaft erfassen
            </DialogTitle>
            <DialogDescription>
              Der Mieter hat statt einer Barkaution eine Mietkautionsbürgschaft (§ 551 BGB)
              gestellt. Es wird KEIN echter Zahlungsfluss gebucht — die Kaution gilt
              intern als vollständig gedeckt.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Aussteller</label>
              <Input
                placeholder="z. B. Sparkasse Bonn / Allianz Versicherung"
                value={guarantee.issuer}
                onChange={(e) => setGuarantee({ ...guarantee, issuer: e.target.value })}
                data-testid="deposit-guarantee-issuer"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Bürgschafts-Nr.</label>
              <Input
                placeholder="Urkunden-Nr."
                value={guarantee.reference}
                onChange={(e) => setGuarantee({ ...guarantee, reference: e.target.value })}
                data-testid="deposit-guarantee-reference"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Eingang am</label>
              <Input
                type="date"
                value={guarantee.received_date}
                onChange={(e) => setGuarantee({ ...guarantee, received_date: e.target.value })}
                data-testid="deposit-guarantee-received"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Gültig bis (optional)</label>
              <Input
                type="date"
                value={guarantee.valid_until}
                onChange={(e) => setGuarantee({ ...guarantee, valid_until: e.target.value })}
                data-testid="deposit-guarantee-valid-until"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Betrag (€, Standard: Kaution)</label>
              <Input type="number" step="0.01" value={guarantee.amount || ""}
                     onChange={(e) => setGuarantee({ ...guarantee, amount: e.target.value })} data-testid="deposit-guarantee-amount" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">Ausgestellt am</label>
              <Input type="date" value={guarantee.issue_date || ""}
                     onChange={(e) => setGuarantee({ ...guarantee, issue_date: e.target.value })} data-testid="deposit-guarantee-issue-date" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Art der Bürgschaft</label>
              <select value={guarantee.guarantee_type || ""} onChange={(e) => setGuarantee({ ...guarantee, guarantee_type: e.target.value })}
                      className="w-full h-9 border border-slate-300 rounded-md px-2 text-sm bg-white" data-testid="deposit-guarantee-type">
                <option value="">— bitte wählen —</option>
                <option value="selbstschuldnerisch">Selbstschuldnerisch (Verzicht auf Einrede der Vorausklage)</option>
                <option value="erstes_anfordern">Auf erstes Anfordern</option>
                <option value="sonstige">Sonstige</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Bürgschaftsurkunde (PDF/Foto)</label>
              <Input type="file" accept="application/pdf,image/*" onChange={(e) => setGuaranteeFile(e.target.files?.[0] || null)}
                     data-testid="deposit-guarantee-file" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-slate-700 mb-1">Notiz (optional)</label>
              <Textarea
                rows={2}
                placeholder="Freitext"
                value={guarantee.note}
                onChange={(e) => setGuarantee({ ...guarantee, note: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setGuaranteeDialog(false)}>Abbrechen</Button>
            <Button className="bg-teal-700 hover:bg-teal-800" onClick={submitGuarantee} data-testid="deposit-guarantee-submit">
              Bürgschaft speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bewegungshistorie */}
      <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold uppercase text-slate-700">
          Bewegungshistorie ({(data.movements || []).length})
        </div>
        <table className="w-full text-sm">
          <thead className="text-left">
            <tr>
              <Th>Datum</Th><Th>Art</Th><Th>Notiz</Th><Th className="text-right">Betrag</Th><Th className="w-12"></Th>
            </tr>
          </thead>
          <tbody>
            {(data.movements || []).length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-slate-500 text-sm">Noch keine Bewegungen erfasst.</td></tr>
            )}
            {(data.movements || []).map((m) => (
              <tr key={m.id} className={`border-t border-slate-100 ${m.third_party_payer ? "bg-amber-50/50" : ""} ${m.kind === "guarantee" ? "bg-teal-50/40" : ""}`} data-testid={`deposit-mov-${m.id}`}>
                <Td className="tabular-nums text-xs whitespace-nowrap">{formatDate(m.booking_date)}</Td>
                <Td className="text-xs">
                  {m.kind === "guarantee" ? (
                    <span className="inline-flex items-center gap-1 text-teal-800 font-medium">
                      <ShieldStar size={12} /> Bürgschaft
                    </span>
                  ) : (
                    { payment: "Zahlung Mieter", payout: "Auszahlung", interest_credit: "Zinsgutschrift", fee: "Gebühr" }[m.kind] || m.kind
                  )}
                </Td>
                <Td className="text-xs text-slate-600">
                  {m.reference || "—"}
                  {m.third_party_payer && (
                    <div className="mt-0.5 inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-900" data-testid={`deposit-mov-tp-badge-${m.id}`}>
                      <UserCircle size={10} />
                      Drittzahler{m.third_party_payer_name ? `: ${m.third_party_payer_name}` : ""}
                      {m.assignment_declaration_received ? (
                        <ShieldCheck size={10} className="text-emerald-700" />
                      ) : (
                        <Warning size={10} className="text-rose-700" />
                      )}
                    </div>
                  )}
                </Td>
                <Td className={`text-right tabular-nums font-medium ${m.kind === "guarantee" ? "text-teal-700" : (m.kind === "payment" || m.kind === "interest_credit" ? "text-emerald-700" : "text-rose-700")}`}>
                  {m.kind === "guarantee"
                    ? <span title="Bürgschafts-Deckung — kein echter Zahlungsfluss">{formatEUR(m.amount)}<span className="ml-1 text-[9px] uppercase tracking-wider text-teal-600">nur Deckung</span></span>
                    : formatEUR(m.amount)}
                </Td>
                <Td className="text-right whitespace-nowrap">
                  {m.kind === "payment" && (
                    <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-amber-700"
                            onClick={() => setTpDialog({
                              movement_id: m.id,
                              third_party_payer: !!m.third_party_payer,
                              third_party_payer_name: m.third_party_payer_name || "",
                              third_party_payer_kind: m.third_party_payer_kind || "jobcenter",
                              assignment_declaration_received: !!m.assignment_declaration_received,
                              assignment_declaration_note: m.assignment_declaration_note || "",
                            })}
                            title="Drittzahler markieren / Abtretung erfassen"
                            data-testid={`deposit-mov-tp-btn-${m.id}`}>
                      <UserCircle size={12} />
                    </Button>
                  )}
                  <Button size="icon" variant="ghost" className="h-7 w-7 hover:text-rose-700" onClick={() => deleteMovement(m.id)} data-testid={`deposit-mov-delete-${m.id}`}>
                    <Trash size={12} />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 2026-02 — Drittzahler-Dialog */}
      <Dialog open={!!tpDialog} onOpenChange={(o) => !o && setTpDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="deposit-tp-dialog">
          <DialogHeader>
            <DialogTitle>Drittzahler & Abtretung</DialogTitle>
            <DialogDescription>
              Kennzeichne diese Kautionszahlung als von einem Dritten (z. B. Jobcenter,
              Familie) geleistet. Ohne vorliegende Abtretungserklärung darf die Kaution
              am Vertragsende NICHT an den Mieter, sondern nur an den Drittzahler
              ausgezahlt werden.
            </DialogDescription>
          </DialogHeader>
          {tpDialog && (
            <div className="space-y-3 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" className="h-4 w-4 accent-amber-600"
                       checked={tpDialog.third_party_payer}
                       onChange={(e) => setTpDialog({ ...tpDialog, third_party_payer: e.target.checked })}
                       data-testid="deposit-tp-flag" />
                <span>Diese Zahlung stammt von einem Drittzahler</span>
              </label>
              {tpDialog.third_party_payer && (
                <>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Art</label>
                    <Select value={tpDialog.third_party_payer_kind}
                            onValueChange={(v) => setTpDialog({ ...tpDialog, third_party_payer_kind: v })}>
                      <SelectTrigger data-testid="deposit-tp-kind"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="jobcenter">Jobcenter / Sozialamt</SelectItem>
                        <SelectItem value="familie">Familie / Angehörige</SelectItem>
                        <SelectItem value="arbeitgeber">Arbeitgeber</SelectItem>
                        <SelectItem value="sonstiges">Sonstiges</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Name des Drittzahlers</label>
                    <Input placeholder='z. B. "Jobcenter Bonn"'
                           value={tpDialog.third_party_payer_name}
                           onChange={(e) => setTpDialog({ ...tpDialog, third_party_payer_name: e.target.value })}
                           data-testid="deposit-tp-name" />
                  </div>
                  <label className="flex items-start gap-2 cursor-pointer border-t border-slate-100 pt-3">
                    <input type="checkbox" className="h-4 w-4 mt-0.5 accent-emerald-600"
                           checked={tpDialog.assignment_declaration_received}
                           onChange={(e) => setTpDialog({ ...tpDialog, assignment_declaration_received: e.target.checked })}
                           data-testid="deposit-tp-assignment" />
                    <span>
                      <span className="font-medium text-slate-900">Abtretungserklärung liegt vor</span>
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        Nur mit vorliegender Abtretung darf die Kaution am Vertragsende an den Mieter ausgezahlt werden.
                      </span>
                    </span>
                  </label>
                  <div>
                    <label className="text-xs font-medium text-slate-600">Notiz (optional)</label>
                    <Textarea rows={2} placeholder="z. B. Aktenzeichen, Datum der Abtretung"
                              value={tpDialog.assignment_declaration_note}
                              onChange={(e) => setTpDialog({ ...tpDialog, assignment_declaration_note: e.target.value })}
                              data-testid="deposit-tp-note" />
                  </div>
                </>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTpDialog(null)}>Abbrechen</Button>
            <Button className="bg-amber-700 hover:bg-amber-800" data-testid="deposit-tp-save"
                    onClick={async () => {
                      try {
                        await api.patch(`/deposit-accounts/${tenancyId}/movements/${tpDialog.movement_id}/third-party`, {
                          third_party_payer: tpDialog.third_party_payer,
                          third_party_payer_name: tpDialog.third_party_payer_name || null,
                          third_party_payer_kind: tpDialog.third_party_payer_kind || null,
                          assignment_declaration_received: tpDialog.assignment_declaration_received,
                          assignment_declaration_note: tpDialog.assignment_declaration_note || null,
                        });
                        toast.success("Drittzahler-Info gespeichert");
                        setTpDialog(null);
                        load();
                      } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
                    }}>
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-02 — Auszahlungs-Warnhinweis */}
      <Dialog open={payoutWarn} onOpenChange={setPayoutWarn}>
        <DialogContent className="max-w-md" data-testid="deposit-payout-warn">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-800">
              <Warning size={20} /> Abtretungserklärung fehlt!
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-700 mt-2">
              Auf diesem Kautionskonto sind Zahlungen von Drittzahlern erfasst
              {data.third_party_payer_names.length > 0 && <> ({data.third_party_payer_names.join(", ")})</>},
              für die noch KEINE Abtretungserklärung hinterlegt ist.
              <div className="mt-2 font-semibold text-rose-800">
                Ohne vorliegende Abtretungserklärung ist eine Auszahlung an den Mieter
                rechtlich nicht zulässig.
              </div>
              <div className="mt-2 text-xs text-slate-600">
                Bitte prüfe zuerst, ob die Abtretung eingegangen ist. Falls ja, öffne die
                betreffende Kautionszahlung und markiere die Abtretung als erhalten.
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPayoutWarn(false)} data-testid="deposit-payout-warn-cancel">
              Zurück
            </Button>
            <Button className="bg-rose-700 hover:bg-rose-800" onClick={confirmPayoutAnyway}
                    data-testid="deposit-payout-warn-confirm">
              Trotzdem auszahlen (auf eigenes Risiko)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// 2026-03 — Kaution → Mietkonto Umbuchung
function DepositTransferPanel({ tenancyId, balance, open, onOpenChange, onDone }) {
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("rent_cold");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState("Letzter Monat mit Kaution verrechnet");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setAmount(balance > 0 ? String(balance) : "");
    // eslint-disable-next-line
  }, [open]);

  const submit = async () => {
    if (!amount || parseFloat(amount) <= 0) { toast.error("Bitte Betrag eingeben"); return; }
    setBusy(true);
    try {
      await api.post(`/deposit-accounts/${tenancyId}/transfer-to-rent`, {
        amount: parseFloat(amount),
        category, booking_date: date, reference,
      });
      toast.success("Kaution im Mietkonto verrechnet");
      onOpenChange?.(false);
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Umbuchung fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="deposit-transfer-dialog">
        <DialogHeader>
          <DialogTitle>Kaution → Mietkonto</DialogTitle>
          <DialogDescription>
            Verfügbares Guthaben: <b>{formatEUR(balance)}</b>. Der eingegebene Betrag wird als
            Zahlung im Mietkonto gebucht und aus dem Kautionskonto abgezogen.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Betrag (€)</label>
            <Input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                   data-testid="deposit-transfer-amount" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kategorie im Mietkonto</label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="deposit-transfer-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="rent_cold">Kaltmiete</SelectItem>
                <SelectItem value="utility_advance">Betriebskosten-Vorauszahlung</SelectItem>
                <SelectItem value="heating_advance">Heizkosten-Vorauszahlung</SelectItem>
                <SelectItem value="parking">Stellplatz</SelectItem>
                <SelectItem value="dunning_fee">Mahngebühren</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Buchungsdatum</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                   data-testid="deposit-transfer-date" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Notiz</label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)}
                   data-testid="deposit-transfer-ref" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || balance <= 0}
                  className="bg-teal-700 hover:bg-teal-800"
                  data-testid="deposit-transfer-submit">
            {busy ? "Bucht…" : "Umbuchen"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// 2026-03 — Timeline aller Kaution→Miete-Umbuchungen
function DepositTransferHistory({ tenancyId, onChanged }) {
  const [items, setItems] = useState(null);
  const load = async () => {
    try {
      const { data } = await api.get(`/deposit-accounts/${tenancyId}/rent-transfers`);
      setItems(data?.items || []);
    } catch { setItems([]); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenancyId]);
  if (!items || !items.length) return null;
  const revoke = async (mid) => {
    if (!await window._domivusConfirm("Diese Umbuchung widerrufen? Der Ist-Posting im Mietkonto wird entfernt, der Kautionssaldo steigt entsprechend wieder.")) return;
    try {
      await api.post(`/deposit-accounts/${tenancyId}/rent-transfers/${mid}/revoke`);
      toast.success("Umbuchung widerrufen");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Widerruf fehlgeschlagen"); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="deposit-transfer-history">
      <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 text-xs font-semibold uppercase text-slate-700 flex items-center justify-between">
        <span>Umbuchungs-Historie · Kaution → Miete ({items.length})</span>
      </div>
      <ol className="divide-y divide-slate-100">
        {items.map((it) => (
          <li key={it.movement_id} className="px-4 py-2 flex items-start gap-3 text-sm" data-testid={`transfer-row-${it.movement_id}`}>
            <div className={`mt-1 h-2 w-2 rounded-full ${it.revoked ? "bg-slate-300" : "bg-teal-500"}`} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium tabular-nums">{formatDate(it.booking_date)}</span>
                <span className="text-slate-700 font-semibold tabular-nums">{formatEUR(it.amount)}</span>
                {it.target_category && (
                  <span className="text-[10px] uppercase tracking-wider text-slate-500 border border-slate-200 rounded px-1.5 py-0.5">
                    {({ rent_cold: "Kaltmiete", utility_advance: "BK-VZ", heating_advance: "HK-VZ", parking: "Stellplatz", dunning_fee: "Mahngebühr" }[it.target_category]) || it.target_category}
                  </span>
                )}
                {it.revoked && (
                  <span className="text-[10px] uppercase tracking-wider text-rose-700 border border-rose-200 bg-rose-50 rounded px-1.5 py-0.5" data-testid={`transfer-revoked-${it.movement_id}`}>
                    widerrufen
                  </span>
                )}
                {!it.revoked && !it.posting_still_present && (
                  <span className="text-[10px] uppercase tracking-wider text-amber-700 border border-amber-200 bg-amber-50 rounded px-1.5 py-0.5" title="Der zugehörige Ist-Posting existiert nicht mehr — möglicherweise separat gelöscht.">
                    Posting fehlt
                  </span>
                )}
              </div>
              {it.reference && (
                <div className="text-xs text-slate-500 mt-0.5">{it.reference}</div>
              )}
              {it.revoked && it.revoked_at && (
                <div className="text-[10px] text-slate-400 mt-0.5">Widerrufen am {formatDate(it.revoked_at)}</div>
              )}
            </div>
            {!it.revoked && (
              <Button size="sm" variant="ghost" className="h-7 text-rose-700 hover:bg-rose-50"
                      onClick={() => revoke(it.movement_id)}
                      data-testid={`transfer-revoke-${it.movement_id}`}>
                Widerrufen
              </Button>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}



// 2026-03 — Kaution nachträglich in Bürgschaft umwandeln
function ConvertToGuaranteePanel({ tenancyId, deposit, open, onOpenChange, onDone }) {
  const [issuer, setIssuer] = useState("");
  const [reference, setReference] = useState("");
  const [received, setReceived] = useState(new Date().toISOString().slice(0, 10));
  const [originalKept, setOriginalKept] = useState(true);
  const [refundIban, setRefundIban] = useState("");
  const [refundHolder, setRefundHolder] = useState("");
  const [busy, setBusy] = useState(false);
  const balance = Number(deposit?.balance || 0);

  const submit = async () => {
    if (!issuer.trim()) { toast.error("Bitte Aussteller angeben"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/deposit-accounts/${tenancyId}/convert-to-guarantee`, {
        issuer: issuer.trim(),
        guarantee_reference: reference.trim() || null,
        received_at: received,
        original_kept: originalKept,
        refund_payments_iban: refundIban.trim() || null,
        refund_payments_holder: refundHolder.trim() || null,
      });
      if (data.original_missing_warning) {
        toast.warning("Umwandlung gespeichert — bitte Original der Bürgschaft nachreichen und bestätigen.");
      } else {
        toast.success(`Kaution in Bürgschaft umgewandelt${data.refund_amount ? ` · ${data.refund_amount.toFixed(2).replace('.', ',')} € Rückzahlung erzeugt` : ""}`);
      }
      onOpenChange?.(false); onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Umwandlung fehlgeschlagen");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="convert-guarantee-dialog">
        <DialogHeader>
          <DialogTitle>Kaution → Bürgschaft</DialogTitle>
          <DialogDescription>
            Der Kautionsbetrag bleibt unverändert. Beim Speichern werden die Bürgschafts-
            Metadaten hinterlegt.{balance > 0.01 ? ` Aktuell eingezahlt: ${balance.toFixed(2).replace('.', ',')} € — kann optional an eine IBAN zurückgezahlt werden.` : ""}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold uppercase text-slate-500">Aussteller *</label>
            <Input value={issuer} onChange={(e) => setIssuer(e.target.value)}
                   placeholder="z. B. Sparkasse Musterstadt oder Erika Mustermann (Elternteil)"
                   data-testid="cg-issuer" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Aktenzeichen</label>
              <Input value={reference} onChange={(e) => setReference(e.target.value)}
                     placeholder="Bürgschaftsnummer" data-testid="cg-ref" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase text-slate-500">Erhalten am</label>
              <Input type="date" value={received} onChange={(e) => setReceived(e.target.value)}
                     data-testid="cg-received" />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={originalKept} onChange={(e) => setOriginalKept(e.target.checked)}
                   className="rounded border-slate-300" data-testid="cg-original" />
            Original der Bürgschaftsurkunde erhalten und archiviert
          </label>
          {!originalKept && (
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Warnung: Ohne archiviertes Original ist die Bürgschaft im Streitfall nicht durchsetzbar. Bitte zeitnah nachreichen.
            </div>
          )}
          {balance > 0.01 && (
            <div className="border-t border-slate-100 pt-2">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
                Rückzahlung der bar hinterlegten Kaution (optional)
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Input value={refundIban} onChange={(e) => setRefundIban(e.target.value)}
                       placeholder="Empfänger-IBAN"
                       data-testid="cg-refund-iban" />
                <Input value={refundHolder} onChange={(e) => setRefundHolder(e.target.value)}
                       placeholder="Kontoinhaber (optional)"
                       data-testid="cg-refund-holder" />
              </div>
              <div className="text-[10px] text-slate-500 mt-1">
                Wenn Sie eine IBAN eintragen, wird eine Auszahlungs-Bewegung über {balance.toFixed(2).replace('.', ',')} € angelegt.
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)}>Abbrechen</Button>
          <Button onClick={submit} disabled={busy || !issuer.trim()}
                  className="bg-indigo-700 hover:bg-indigo-800"
                  data-testid="cg-submit">
            {busy ? "Speichert…" : "Umwandeln"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// 2026-03 — Overflow-Menü mit Umbuchen + Bürgschaft-Umwandeln
function DepositActionsMenu({ tenancyId, data, onChanged }) {
  const [transferOpen, setTransferOpen] = useState(false);
  const [guaranteeOpen, setGuaranteeOpen] = useState(false);
  const balance = Number(data?.balance || 0);
  const currentType = data?.tenancy?.deposit_type || data?.deposit_type;
  const canTransfer = balance > 0;
  const canConvert = currentType !== "buergschaft";
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="icon" className="h-8 w-8"
                  data-testid="deposit-actions-menu" title="Weitere Aktionen">
            <DotsThreeVertical size={14} weight="bold" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-72">
          <DropdownMenuLabel className="text-[10px] uppercase text-slate-500">Kautions-Aktionen</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={async () => {
              try {
                const res = await api.get(`/deposit-accounts/${tenancyId}/statement.pdf`, { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url;
                a.download = `Kautionsauszug_${tenancyId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
                toast.success("Kautionsauszug heruntergeladen");
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Kautionsauszug konnte nicht erzeugt werden");
              }
            }}
            className="cursor-pointer"
            data-testid="deposit-statement-pdf-btn"
          >
            <FileText size={14} className="mr-2 text-slate-700" />
            <div className="flex flex-col">
              <span>Kautionsauszug PDF</span>
              <span className="text-[10px] text-slate-500">1-seitige Übersicht (Raten, Bewegungen, Zinsen)</span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setTransferOpen(true)} disabled={!canTransfer}
                            className="cursor-pointer" data-testid="deposit-transfer-btn">
            <ArrowsLeftRight size={14} className="mr-2 text-teal-700" />
            <div className="flex flex-col">
              <span>Mit Mietkonto verrechnen</span>
              <span className="text-[10px] text-slate-500">
                {canTransfer ? `Teilbetrag aus ${balance.toFixed(2).replace('.', ',')} € umbuchen` : "Kein Kautionsguthaben"}
              </span>
            </div>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setGuaranteeOpen(true)} disabled={!canConvert}
                            className="cursor-pointer" data-testid="convert-guarantee-btn">
            <ShieldStar size={14} className="mr-2 text-indigo-700" />
            <div className="flex flex-col">
              <span>In Bürgschaft umwandeln</span>
              <span className="text-[10px] text-slate-500">
                {canConvert ? "Nach Erhalt der Bürgschaftsurkunde" : "Bereits Bürgschaft hinterlegt"}
              </span>
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <DepositTransferPanel tenancyId={tenancyId} balance={balance}
                            open={transferOpen} onOpenChange={setTransferOpen}
                            onDone={onChanged} />
      <ConvertToGuaranteePanel tenancyId={tenancyId} deposit={data}
                               open={guaranteeOpen} onOpenChange={setGuaranteeOpen}
                               onDone={onChanged} />
    </>
  );
}

